import { createContext, useContext, useMemo, useState } from 'react';

import { useGraphQLQuery } from '@betterment/graphql';
import { useDebouncedCallback } from 'use-debounce';

import { ManagedAccountProjectionInput } from '../../../graphql/schema';
import { ForecasterAccountSettingsDocument } from '../graphql/forecasterAccountSettings.generated';

export const ForecasterAccountInputsContext = createContext<{
  formInputs?: ManagedAccountProjectionInput;
  updateFormInputs: (input: ManagedAccountProjectionInput) => void;
} | null>(null);

type Props = {
  accountId: string;
  children: React.ReactNode;
};

export const useForecasterAccountSettings = (accountId: string) => {
  return useGraphQLQuery(ForecasterAccountSettingsDocument, {
    variables: { accountId },
    throwOnError: true,
  });
};

export const ForecasterAccountInputsProvider: React.FC<Props> = ({ accountId, children }) => {
  const [formInputs, setFormInputs] = useState<ManagedAccountProjectionInput>();

  const debounce = useDebouncedCallback((callback: () => void) => callback(), 500);
  const value = useMemo(
    () => ({
      formInputs,
      updateFormInputs: (input: ManagedAccountProjectionInput) =>
        debounce.callback(() => setFormInputs(prev => ({ ...prev, ...input }))),
    }),
    [formInputs]
  );

  return <ForecasterAccountInputsContext.Provider value={value}>{children}</ForecasterAccountInputsContext.Provider>;
};

export const useForecasterAccountInputs = () => {
  const context = useContext(ForecasterAccountInputsContext);
  if (!context) {
    throw new Error('useForecasterAccountInputs must be used within a ForecasterAccountInputsProvider');
  }
  return context;
};
